import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, NavLink, useParams, useNavigate } from "react-router-dom";
import { LayoutDashboard, Layers, FlaskConical, History, ChevronRight, Loader2, Boxes, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ProjectContext = createContext({ project: null, refresh: () => {} });

export function useProject() {
  return useContext(ProjectContext);
}

export default function ProjectLayout() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const navigate = useNavigate();

  const reload = () => {
    api.get(`/projects/${projectId}`)
      .then((r) => setProject(r.data))
      .catch((e) => {
        toast.error(formatApiError(e));
        navigate("/projects");
      });
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${projectId}`)
      .then((r) => { if (!cancelled) setProject(r.data); })
      .catch((e) => {
        if (cancelled) return;
        toast.error(formatApiError(e));
        navigate("/projects");
      });
    return () => { cancelled = true; };
  }, [projectId, navigate]);

  if (!project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10 text-sm text-[#64748B] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading project…
      </div>
    );
  }

  const tabs = [
    { to: `/projects/${projectId}`, end: true, label: "Overview", icon: LayoutDashboard, testId: "tab-overview" },
    { to: `/projects/${projectId}/environments`, label: "Environments", icon: Boxes, testId: "tab-environments" },
    { to: `/projects/${projectId}/suites`, label: "Suites", icon: Layers, testId: "tab-suites" },
    { to: `/projects/${projectId}/tests`, label: "Tests", icon: FlaskConical, testId: "tab-tests" },
    { to: `/projects/${projectId}/runs`, label: "Runs", icon: History, testId: "tab-runs" },
    { to: `/projects/${projectId}/schedules`, label: "Schedules", icon: CalendarClock, testId: "tab-schedules" },
  ];

  return (
    <ProjectContext.Provider value={{ project, refresh: reload }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-8 sm:pt-10" data-testid="project-layout">
        <div className="flex items-center gap-1.5 text-xs text-[#64748B] mb-3">
          <button onClick={() => navigate("/projects")} className="hover:text-[#3B82F6] transition-colors" data-testid="breadcrumb-projects">
            Projects
          </button>
          <ChevronRight size={12} />
          <span className="text-[#0F172A] font-medium truncate">{project.name}</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="font-['Geist'] font-semibold text-2xl sm:text-[28px] tracking-tight text-[#0F172A] leading-tight">
              {project.name}
            </h1>
            {project.description && (
              <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed max-w-2xl">
                {project.description}
              </p>
            )}
          </div>
        </div>
        <div className="border-b border-[#E5E7EB] mb-6 -mx-1 px-1 overflow-x-auto">
          <nav className="flex gap-1" data-testid="project-tabs">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  data-testid={t.testId}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 px-3 h-10 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? "border-[#3B82F6] text-[#0F172A]"
                        : "border-transparent text-[#64748B] hover:text-[#0F172A]"
                    }`
                  }
                >
                  <Icon size={14} />
                  {t.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
        <div className="pb-10">
          <Outlet />
        </div>
      </div>
    </ProjectContext.Provider>
  );
}
